import React, { useState } from "react";
import { useListRecommendations, RecommendationGroup, RecommendationRule, useCopyLastRecommendations, useDeleteRecommendation } from "@/lib/api/risk/recommendation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Edit, ChevronLeft, ChevronRight, Copy, Trash2 } from "lucide-react";
import { CommodityOptions } from "@/lib/api/commodity";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RecommendationForm } from "./Form";
import { getConstraintDisplayLabel } from "@/lib/api/risk/recommendation/index";
import { useToast } from "@/components/ui/use-toast";
import moment from "moment";

interface DashboardProps {
  className?: string;
}

function formatRuleDisplay(rule: RecommendationRule): string {
  const constraintLabel = getConstraintDisplayLabel(rule.constraint_type);
  const rangeType = rule.range_type;
  const value = rule.range_constraint_value;
  
  let condition: string;
  if (rangeType === "between") {
    const [min, max] = value.split("-");
    condition = `${constraintLabel} is between ${min}% and ${max}%`;
  } else {
    condition = `${constraintLabel} ${rangeType} ${value}%`;
  }
  
  return `If ${condition} then "${rule.message}"`;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "An unknown error occurred";
}

export function RiskManagementRecommendationsDashobard({ className }: DashboardProps) {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<RecommendationGroup | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  const listRecommendationsResult = useListRecommendations({
    date: selectedDate,
  });

  const { isLoading, error, refetch } = listRecommendationsResult;
  const listRecommendationData = listRecommendationsResult?.data?.data;
  const copyLastRecommendations = useCopyLastRecommendations();
  const deleteRecommendation = useDeleteRecommendation();

  // Group recommendations by commodity and crop year
  const groupedRecommendations = React.useMemo(() => {
    if (!listRecommendationData) return {};

    const grouped: Record<
      string,
      Record<number, RecommendationGroup[]>
    > = {};

    listRecommendationData.forEach((group: RecommendationGroup) => {
      const commodity = group.commodity_type;
      const cropYear = group.crop_year;

      if (!grouped[commodity]) {
        grouped[commodity] = {};
      }
      if (!grouped[commodity][cropYear]) {
        grouped[commodity][cropYear] = [];
      }
      grouped[commodity][cropYear].push(group);
    });

    return grouped;
  }, [listRecommendationData]);

  const commodities = Object.keys(groupedRecommendations).sort();

  const handleCreate = () => {
    setEditingGroup(null);
    setIsFormOpen(true);
  };

  const handleEdit = (group: RecommendationGroup) => {
    setEditingGroup(group);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingGroup(null);
    refetch();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingGroup(null);
  };

  const handlePreviousDate = () => {
    const newDate = moment(selectedDate).subtract(1, "day").format("YYYY-MM-DD");
    setSelectedDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = moment(selectedDate).add(1, "day").format("YYYY-MM-DD");
    setSelectedDate(newDate);
  };

  const handleCopyLast = async () => {
    setIsCopying(true);
    try {
      await copyLastRecommendations();
    } catch(error) {
      toast({
        title: "Failed to copy last recommendations"
      })
    }finally {
      setIsCopying(false);
    }
  };

  const handleDelete = async (group: RecommendationGroup) => {
    try {
      await deleteRecommendation(
        moment.tz(group.recommendation_date, "UTC").format("YYYY-MM-DD"),
        group.crop_year,
        group.commodity_type
      );
      toast({
        title: "Recommendation deleted successfully"
      });
    } catch(error) {
      toast({
        title: "Failed to delete recommendation"
      });
    }
  };

  return (
    <div className={className}>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              <label htmlFor="date-select" className="text-sm font-medium">
                Select Date
              </label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePreviousDate}
                  className="h-10 w-10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <input
                  id="date-select"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="flex h-10 w-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNextDate}
                  className="h-10 w-10"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCopyLast} disabled={isCopying}>
              {isCopying ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copy Last
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Recommendation
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {error ? (
          <div className="text-destructive p-4 border border-destructive rounded">
            Error loading recommendations: {getErrorMessage(error)}
          </div>
        ) : null}

        {!isLoading && !error && commodities.length === 0 && (
          <div className="text-muted-foreground p-4 border rounded">
            No recommendations found for {selectedDate}
          </div>
        )}

        {!isLoading && !error && commodities.length > 0 && (
          <div className="space-y-1">
            {commodities.map((commodity_type) => {
              const commodity_label = CommodityOptions.find(item => item.value === commodity_type)?.label || commodity_type;
              const cropYears = Object.keys(groupedRecommendations[commodity_type])
                .map(Number)
                .sort((a, b) => b - a);

              return (
                <Card key={`${commodity_type}`}>
                  <CardHeader>
                    <CardTitle className="text-xl">{commodity_label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {cropYears.map((cropYear) => {
                        const groups =
                          groupedRecommendations[commodity_type][cropYear];
                        const allRules = groups.flatMap(
                          (g) => g.RecommendationRules
                        );

                        return (
                          <div key={cropYear} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <Badge variant="secondary">
                                  Crop Year: {cropYear}
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  ({allRules.length} rule{allRules.length !== 1 ? "s" : ""})
                                </span>
                              </div>
                              {groups.length > 0 && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEdit(groups[0])}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDelete(groups[0])}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="ml-4 space-y-1">
                              {allRules.map((rule) => (
                                <div
                                  key={rule.id}
                                  className="p-3 border rounded-md bg-muted/50"
                                >
                                  <p className="text-sm font-mono">
                                    {formatRuleDisplay(rule)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={isFormOpen} onOpenChange={(open) => !open && handleFormCancel()}>
        <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[80vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Edit Recommendation" : "Create Recommendation"}
            </DialogTitle>
          </DialogHeader>
          <RecommendationForm
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
            showCancelButton={true}
            initialDate={selectedDate}
            initialCommodity={editingGroup?.commodity_type}
            initialCropYear={editingGroup?.crop_year}
            existingGroupId={editingGroup?.id}
            existingGroup={editingGroup || undefined}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
